export const logicDiscreteStructuresCourse = {
  id: 'logic-discrete-structures',
  title: 'Logic and Discrete Structures',
  description: 'Fundamental concepts of mathematical logic, set theory, relations, functions, and discrete mathematical structures essential for computer science.',
  category: 'Coding',
  credits: 2,
  duration: '15 weeks',
  color: 'indigo',
  modules: [
    {
      id: 'module-1',
      title: 'Propositional Logic and Boolean Algebra',
      description: 'Learn the foundations of logical reasoning, truth tables, logical operators, and Boolean algebra applications in computer science.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'lesson-1',
          title: 'Propositional Logic Basics',
          type: 'coding-interactive',
          content: 'Understanding propositions, logical connectives, and truth values in computational contexts.',
          exercise: {
            instructions: 'Create a function "evaluateLogicalExpression" that takes a logical expression as a string and variable values, then returns the boolean result. Support AND (&), OR (|), NOT (!), and variables. Test with "A & B | !C" where A=true, B=false, C=true should return false.',
            initialCode: `function evaluateLogicalExpression(expression, variables) {
  // Parse and evaluate logical expressions
  // expression: string like "A & B | !C"
  // variables: object like {A: true, B: false, C: true}
  
  let expr = expression;
  
  // Replace variables with their values
  for (let variable in variables) {
    const regex = new RegExp(variable, 'g');
    expr = expr.replace(regex, variables[variable]);
  }
  
  // Replace logical operators with JavaScript operators
  expr = expr.replace(/&/g, '&&');
  expr = expr.replace(/\\|/g, '||');
  expr = expr.replace(/!/g, '!');
  
  // Evaluate the expression
  try {
    return eval(expr);
  } catch (error) {
    return false;
  }
}

// Enhanced version with proper parsing
function evaluateLogicalExpressionAdvanced(expression, variables) {
  // Tokenize the expression
  const tokens = expression.match(/[A-Z]|[&|!()]/g) || [];
  
  // Convert infix to postfix notation (Shunting Yard algorithm)
  const precedence = {'!': 3, '&': 2, '|': 1};
  const outputQueue = [];
  const operatorStack = [];
  
  tokens.forEach(token => {
    if (/[A-Z]/.test(token)) {
      outputQueue.push(variables[token]);
    } else if (token === '!') {
      operatorStack.push(token);
    } else if (token === '&' || token === '|') {
      while (operatorStack.length > 0 && 
             operatorStack[operatorStack.length - 1] !== '(' &&
             precedence[operatorStack[operatorStack.length - 1]] >= precedence[token]) {
        outputQueue.push(operatorStack.pop());
      }
      operatorStack.push(token);
    } else if (token === '(') {
      operatorStack.push(token);
    } else if (token === ')') {
      while (operatorStack[operatorStack.length - 1] !== '(') {
        outputQueue.push(operatorStack.pop());
      }
      operatorStack.pop(); // Remove the '('
    }
  });
  
  while (operatorStack.length > 0) {
    outputQueue.push(operatorStack.pop());
  }
  
  // Evaluate postfix expression
  const stack = [];
  outputQueue.forEach(token => {
    if (typeof token === 'boolean') {
      stack.push(token);
    } else if (token === '!') {
      const operand = stack.pop();
      stack.push(!operand);
    } else if (token === '&') {
      const b = stack.pop();
      const a = stack.pop();
      stack.push(a && b);
    } else if (token === '|') {
      const b = stack.pop();
      const a = stack.pop();
      stack.push(a || b);
    }
  });
  
  return stack[0];
}

// Test the function
console.log(evaluateLogicalExpression("A & B | !C", {A: true, B: false, C: true}));`,
            expectedOutput: `false`,
            hints: [
              'Replace variables with their boolean values first',
              'Convert logical operators to JavaScript equivalents',
              'Use eval() carefully or implement a proper parser'
            ]
          }
        },
        {
          id: 'lesson-2',
          title: 'Truth Tables and Logical Equivalence',
          type: 'coding-interactive',
          content: 'Generating truth tables programmatically and checking logical equivalence between expressions.',
          exercise: {
            instructions: 'Create a function "generateTruthTable" that generates a complete truth table for a logical expression with given variables. Return an array of objects with variable assignments and results. Test with "A & B" which should have 4 rows.',
            initialCode: `function generateTruthTable(expression, variableNames) {
  const truthTable = [];
  const numVariables = variableNames.length;
  const numRows = Math.pow(2, numVariables);
  
  // Generate all possible combinations
  for (let i = 0; i < numRows; i++) {
    const assignment = {};
    const binary = i.toString(2).padStart(numVariables, '0');
    
    // Assign truth values based on binary representation
    for (let j = 0; j < numVariables; j++) {
      assignment[variableNames[j]] = binary[j] === '1';
    }
    
    // Evaluate expression with this assignment
    const result = evaluateLogicalExpression(expression, assignment);
    
    truthTable.push({
      ...assignment,
      result: result
    });
  }
  
  return truthTable;
}

// Helper function from previous lesson
function evaluateLogicalExpression(expression, variables) {
  let expr = expression;
  
  for (let variable in variables) {
    const regex = new RegExp(variable, 'g');
    expr = expr.replace(regex, variables[variable]);
  }
  
  expr = expr.replace(/&/g, '&&');
  expr = expr.replace(/\\|/g, '||');
  expr = expr.replace(/!/g, '!');
  
  try {
    return eval(expr);
  } catch (error) {
    return false;
  }
}

// Function to check logical equivalence
function areLogicallyEquivalent(expr1, expr2, variables) {
  const table1 = generateTruthTable(expr1, variables);
  const table2 = generateTruthTable(expr2, variables);
  
  if (table1.length !== table2.length) return false;
  
  for (let i = 0; i < table1.length; i++) {
    if (table1[i].result !== table2[i].result) {
      return false;
    }
  }
  
  return true;
}

// Test the function
console.log(generateTruthTable("A & B", ["A", "B"]).length);`,
            expectedOutput: `4`,
            hints: [
              'Use binary representation to generate all combinations',
              'Iterate through 2^n possibilities for n variables',
              'Evaluate the expression for each combination'
            ]
          }
        },
        {
          id: 'lesson-3',
          title: 'Boolean Algebra and Simplification',
          type: 'coding-interactive',
          content: 'Applying Boolean algebra laws to simplify logical expressions programmatically.',
          exercise: {
            instructions: 'Create a function "simplifyBooleanExpression" that applies basic Boolean algebra laws to simplify expressions. Implement at least De Morgan\'s laws and basic identities. Test with "!(A & B)" which should return "!A | !B".',
            initialCode: `function simplifyBooleanExpression(expression) {
  let simplified = expression;
  let changed = true;
  
  // Keep applying rules until no more changes
  while (changed) {
    const original = simplified;
    
    // De Morgan's Laws
    // !(A & B) = !A | !B
    simplified = simplified.replace(/!\\(([A-Z])\\s*&\\s*([A-Z])\\)/g, '!$1 | !$2');
    // !(A | B) = !A & !B
    simplified = simplified.replace(/!\\(([A-Z])\\s*\\|\\s*([A-Z])\\)/g, '!$1 & !$2');
    
    // Double negation: !!A = A
    simplified = simplified.replace(/!!([A-Z])/g, '$1');
    
    // Identity laws
    // A & true = A
    simplified = simplified.replace(/([A-Z])\\s*&\\s*true/g, '$1');
    simplified = simplified.replace(/true\\s*&\\s*([A-Z])/g, '$1');
    // A | false = A
    simplified = simplified.replace(/([A-Z])\\s*\\|\\s*false/g, '$1');
    simplified = simplified.replace(/false\\s*\\|\\s*([A-Z])/g, '$1');
    
    // Null laws
    // A & false = false
    simplified = simplified.replace(/([A-Z])\\s*&\\s*false/g, 'false');
    simplified = simplified.replace(/false\\s*&\\s*([A-Z])/g, 'false');
    // A | true = true
    simplified = simplified.replace(/([A-Z])\\s*\\|\\s*true/g, 'true');
    simplified = simplified.replace(/true\\s*\\|\\s*([A-Z])/g, 'true');
    
    // Idempotent laws
    // A & A = A
    simplified = simplified.replace(/([A-Z])\\s*&\\s*\\1/g, '$1');
    // A | A = A
    simplified = simplified.replace(/([A-Z])\\s*\\|\\s*\\1/g, '$1');
    
    // Complement laws
    // A & !A = false
    simplified = simplified.replace(/([A-Z])\\s*&\\s*!\\1/g, 'false');
    simplified = simplified.replace(/!([A-Z])\\s*&\\s*\\1/g, 'false');
    // A | !A = true
    simplified = simplified.replace(/([A-Z])\\s*\\|\\s*!\\1/g, 'true');
    simplified = simplified.replace(/!([A-Z])\\s*\\|\\s*\\1/g, 'true');
    
    // Remove extra spaces
    simplified = simplified.replace(/\\s+/g, ' ').trim();
    
    changed = (original !== simplified);
  }
  
  return simplified;
}

// Advanced simplification with more complex patterns
function advancedSimplify(expression) {
  // This would implement more sophisticated algorithms like:
  // - Karnaugh maps
  // - Quine-McCluskey method
  // - Algebraic manipulation
  
  // For now, apply the basic rules
  return simplifyBooleanExpression(expression);
}

// Function to verify equivalence after simplification
function verifySimplification(original, simplified, variables) {
  try {
    return areLogicallyEquivalent(original, simplified, variables);
  } catch (error) {
    return false;
  }
}

// Test the function
console.log(simplifyBooleanExpression("!(A & B)"));`,
            expectedOutput: `!A | !B`,
            hints: [
              'Apply De Morgan\'s laws: !(A & B) = !A | !B',
              'Use regular expressions for pattern matching',
              'Apply rules iteratively until no changes occur'
            ]
          }
        }
      ],
      assessment: {
        type: 'coding-interactive',
        title: 'Module 1 Assessment: Logic Systems',
        instructions: 'Create a comprehensive "LogicSystem" class that can parse, evaluate, simplify, and generate truth tables for logical expressions. Include methods for checking satisfiability and tautology. Test with various logical expressions.',
        initialCode: `class LogicSystem {
  constructor() {
    this.variables = new Set();
  }
  
  // Parse expression and extract variables
  parseExpression(expression) {
    const variables = expression.match(/[A-Z]/g) || [];
    variables.forEach(v => this.variables.add(v));
    return Array.from(new Set(variables));
  }
  
  // Evaluate expression with given variable assignment
  evaluate(expression, assignment) {
    let expr = expression;
    
    // Replace variables with values
    for (let variable in assignment) {
      const regex = new RegExp(variable, 'g');
      expr = expr.replace(regex, assignment[variable]);
    }
    
    // Replace logical operators
    expr = expr.replace(/&/g, '&&');
    expr = expr.replace(/\\|/g, '||');
    expr = expr.replace(/!/g, '!');
    
    try {
      return eval(expr);
    } catch (error) {
      throw new Error('Invalid expression: ' + expression);
    }
  }
  
  // Generate complete truth table
  generateTruthTable(expression) {
    const variables = this.parseExpression(expression);
    const truthTable = [];
    const numRows = Math.pow(2, variables.length);
    
    for (let i = 0; i < numRows; i++) {
      const assignment = {};
      const binary = i.toString(2).padStart(variables.length, '0');
      
      for (let j = 0; j < variables.length; j++) {
        assignment[variables[j]] = binary[j] === '1';
      }
      
      const result = this.evaluate(expression, assignment);
      truthTable.push({
        ...assignment,
        result: result
      });
    }
    
    return truthTable;
  }
  
  // Check if expression is satisfiable (has at least one true result)
  isSatisfiable(expression) {
    const truthTable = this.generateTruthTable(expression);
    return truthTable.some(row => row.result === true);
  }
  
  // Check if expression is a tautology (always true)
  isTautology(expression) {
    const truthTable = this.generateTruthTable(expression);
    return truthTable.every(row => row.result === true);
  }
  
  // Check if expression is a contradiction (always false)
  isContradiction(expression) {
    const truthTable = this.generateTruthTable(expression);
    return truthTable.every(row => row.result === false);
  }
  
  // Simplify expression using Boolean algebra
  simplify(expression) {
    let simplified = expression;
    let iterations = 0;
    const maxIterations = 10; // Prevent infinite loops
    
    while (iterations < maxIterations) {
      const original = simplified;
      
      // Apply simplification rules
      simplified = this.applySimplificationRules(simplified);
      
      if (original === simplified) break;
      iterations++;
    }
    
    return simplified;
  }
  
  applySimplificationRules(expr) {
    let result = expr;
    
    // De Morgan's Laws
    result = result.replace(/!\\(([A-Z])\\s*&\\s*([A-Z])\\)/g, '!$1 | !$2');
    result = result.replace(/!\\(([A-Z])\\s*\\|\\s*([A-Z])\\)/g, '!$1 & !$2');
    
    // Double negation
    result = result.replace(/!!([A-Z])/g, '$1');
    
    // Identity and null laws
    result = result.replace(/([A-Z])\\s*&\\s*true/g, '$1');
    result = result.replace(/true\\s*&\\s*([A-Z])/g, '$1');
    result = result.replace(/([A-Z])\\s*\\|\\s*false/g, '$1');
    result = result.replace(/false\\s*\\|\\s*([A-Z])/g, '$1');
    result = result.replace(/([A-Z])\\s*&\\s*false/g, 'false');
    result = result.replace(/false\\s*&\\s*([A-Z])/g, 'false');
    result = result.replace(/([A-Z])\\s*\\|\\s*true/g, 'true');
    result = result.replace(/true\\s*\\|\\s*([A-Z])/g, 'true');
    
    // Idempotent laws
    result = result.replace(/([A-Z])\\s*&\\s*\\1/g, '$1');
    result = result.replace(/([A-Z])\\s*\\|\\s*\\1/g, '$1');
    
    // Complement laws
    result = result.replace(/([A-Z])\\s*&\\s*!\\1/g, 'false');
    result = result.replace(/!([A-Z])\\s*&\\s*\\1/g, 'false');
    result = result.replace(/([A-Z])\\s*\\|\\s*!\\1/g, 'true');
    result = result.replace(/!([A-Z])\\s*\\|\\s*\\1/g, 'true');
    
    return result.replace(/\\s+/g, ' ').trim();
  }
  
  // Check logical equivalence between two expressions
  areEquivalent(expr1, expr2) {
    const variables1 = this.parseExpression(expr1);
    const variables2 = this.parseExpression(expr2);
    const allVariables = Array.from(new Set([...variables1, ...variables2]));
    
    const table1 = this.generateTruthTable(expr1);
    const table2 = this.generateTruthTable(expr2);
    
    if (table1.length !== table2.length) return false;
    
    for (let i = 0; i < table1.length; i++) {
      if (table1[i].result !== table2[i].result) {
        return false;
      }
    }
    
    return true;
  }
  
  // Get statistics about the expression
  getStatistics(expression) {
    const truthTable = this.generateTruthTable(expression);
    const trueCount = truthTable.filter(row => row.result).length;
    const totalRows = truthTable.length;
    
    return {
      variables: this.parseExpression(expression),
      truthTable: truthTable,
      satisfiable: trueCount > 0,
      tautology: trueCount === totalRows,
      contradiction: trueCount === 0,
      satisfiabilityRatio: trueCount / totalRows,
      simplified: this.simplify(expression)
    };
  }
}

// Test the LogicSystem
const logic = new LogicSystem();
const expr = "A & B | !A & C";
const stats = logic.getStatistics(expr);
console.log(stats.variables.length);`,
        expectedOutput: `3`,
        hints: [
          'Use proper parsing to extract variables from expressions',
          'Implement truth table generation systematically',
          'Apply Boolean algebra rules for simplification',
          'Check all rows for tautology/contradiction detection'
        ]
      }
    },
    {
      id: 'module-2',
      title: 'Set Theory and Relations',
      description: 'Explore set operations, relations, functions, and their computational representations and applications.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'lesson-1',
          title: 'Set Operations and Representations',
          type: 'coding-interactive',
          content: 'Implementing sets and set operations programmatically using various data structures.',
          exercise: {
            instructions: 'Create a "MathSet" class that implements mathematical set operations including union, intersection, difference, and symmetric difference. Include methods for subset checking and cardinality. Test with sets A={1,2,3} and B={2,3,4} - their union should have 4 elements.',
            initialCode: `class MathSet {
  constructor(elements = []) {
    this.elements = new Set(elements);
  }
  
  // Add element to set
  add(element) {
    this.elements.add(element);
    return this;
  }
  
  // Remove element from set
  remove(element) {
    this.elements.delete(element);
    return this;
  }
  
  // Check if element is in set
  contains(element) {
    return this.elements.has(element);
  }
  
  // Get cardinality (size) of set
  cardinality() {
    return this.elements.size;
  }
  
  // Union of two sets: A ∪ B
  union(otherSet) {
    const result = new MathSet();
    this.elements.forEach(elem => result.add(elem));
    otherSet.elements.forEach(elem => result.add(elem));
    return result;
  }
  
  // Intersection of two sets: A ∩ B
  intersection(otherSet) {
    const result = new MathSet();
    this.elements.forEach(elem => {
      if (otherSet.contains(elem)) {
        result.add(elem);
      }
    });
    return result;
  }
  
  // Difference of two sets: A - B
  difference(otherSet) {
    const result = new MathSet();
    this.elements.forEach(elem => {
      if (!otherSet.contains(elem)) {
        result.add(elem);
      }
    });
    return result;
  }
  
  // Symmetric difference: A ⊕ B = (A - B) ∪ (B - A)
  symmetricDifference(otherSet) {
    const leftDiff = this.difference(otherSet);
    const rightDiff = otherSet.difference(this);
    return leftDiff.union(rightDiff);
  }
  
  // Check if this is a subset of otherSet: A ⊆ B
  isSubsetOf(otherSet) {
    return Array.from(this.elements).every(elem => otherSet.contains(elem));
  }
  
  // Check if this is a proper subset of otherSet: A ⊂ B
  isProperSubsetOf(otherSet) {
    return this.isSubsetOf(otherSet) && this.cardinality() < otherSet.cardinality();
  }
  
  // Check if this is a superset of otherSet: A ⊇ B
  isSupersetOf(otherSet) {
    return otherSet.isSubsetOf(this);
  }
  
  // Check if sets are equal
  equals(otherSet) {
    return this.cardinality() === otherSet.cardinality() && this.isSubsetOf(otherSet);
  }
  
  // Check if sets are disjoint (no common elements)
  isDisjointWith(otherSet) {
    return this.intersection(otherSet).cardinality() === 0;
  }
  
  // Cartesian product with another set: A × B
  cartesianProduct(otherSet) {
    const result = new MathSet();
    this.elements.forEach(a => {
      otherSet.elements.forEach(b => {
        result.add([a, b]);
      });
    });
    return result;
  }
  
  // Power set (set of all subsets)
  powerSet() {
    const elemArray = Array.from(this.elements);
    const powerSetArray = [];
    const n = elemArray.length;
    
    // Generate all 2^n subsets
    for (let i = 0; i < Math.pow(2, n); i++) {
      const subset = [];
      for (let j = 0; j < n; j++) {
        if (i & (1 << j)) {
          subset.push(elemArray[j]);
        }
      }
      powerSetArray.push(new MathSet(subset));
    }
    
    return new MathSet(powerSetArray);
  }
  
  // Convert to array for easy viewing
  toArray() {
    return Array.from(this.elements).sort();
  }
  
  // String representation
  toString() {
    return '{' + this.toArray().join(', ') + '}';
  }
  
  // Get complement with respect to universal set
  complement(universalSet) {
    return universalSet.difference(this);
  }
  
  // Apply a function to all elements (map operation)
  map(func) {
    const result = new MathSet();
    this.elements.forEach(elem => result.add(func(elem)));
    return result;
  }
  
  // Filter elements based on predicate
  filter(predicate) {
    const result = new MathSet();
    this.elements.forEach(elem => {
      if (predicate(elem)) result.add(elem);
    });
    return result;
  }
}

// Test the MathSet class
const setA = new MathSet([1, 2, 3]);
const setB = new MathSet([2, 3, 4]);
const union = setA.union(setB);
console.log(union.cardinality());`,
            expectedOutput: `4`,
            hints: [
              'Use JavaScript Set for efficient element storage',
              'Union combines all unique elements from both sets',
              'Intersection contains only common elements',
              'Implement proper subset checking carefully'
            ]
          }
        },
        {
          id: 'lesson-2',
          title: 'Relations and Their Properties',
          type: 'coding-interactive',
          content: 'Implementing and analyzing mathematical relations including reflexive, symmetric, transitive properties.',
          exercise: {
            instructions: 'Create a "Relation" class that represents binary relations on sets. Implement methods to check if a relation is reflexive, symmetric, transitive, and equivalence. Test with a relation on {1,2,3} that should determine if it\'s an equivalence relation.',
            initialCode: `class Relation {
  constructor(domain, pairs = []) {
    this.domain = new Set(domain);
    this.pairs = new Set();
    
    // Add pairs, ensuring they're in domain
    pairs.forEach(pair => {
      if (this.domain.has(pair[0]) && this.domain.has(pair[1])) {
        this.pairs.add(pair[0] + ',' + pair[1]); // String representation for Set
      }
    });
  }
  
  // Add a pair to the relation
  addPair(a, b) {
    if (this.domain.has(a) && this.domain.has(b)) {
      this.pairs.add(a + ',' + b);
    }
    return this;
  }
  
  // Check if pair (a,b) is in relation
  contains(a, b) {
    return this.pairs.has(a + ',' + b);
  }
  
  // Get all pairs as array of [a,b]
  getPairs() {
    return Array.from(this.pairs).map(pair => {
      const [a, b] = pair.split(',');
      return [isNaN(a) ? a : Number(a), isNaN(b) ? b : Number(b)];
    });
  }
  
  // Check if relation is reflexive (aRa for all a in domain)
  isReflexive() {
    return Array.from(this.domain).every(a => this.contains(a, a));
  }
  
  // Check if relation is symmetric (aRb implies bRa)
  isSymmetric() {
    return this.getPairs().every(([a, b]) => this.contains(b, a));
  }
  
  // Check if relation is antisymmetric (aRb and bRa implies a = b)
  isAntisymmetric() {
    return this.getPairs().every(([a, b]) => {
      if (a !== b && this.contains(b, a)) {
        return false;
      }
      return true;
    });
  }
  
  // Check if relation is transitive (aRb and bRc implies aRc)
  isTransitive() {
    const pairs = this.getPairs();
    
    for (let [a, b] of pairs) {
      for (let [c, d] of pairs) {
        if (b === c) { // Found aRb and bRd
          if (!this.contains(a, d)) {
            return false;
          }
        }
      }
    }
    return true;
  }
  
  // Check if relation is an equivalence relation
  isEquivalence() {
    return this.isReflexive() && this.isSymmetric() && this.isTransitive();
  }
  
  // Check if relation is a partial order
  isPartialOrder() {
    return this.isReflexive() && this.isAntisymmetric() && this.isTransitive();
  }
  
  // Get equivalence classes (if it's an equivalence relation)
  getEquivalenceClasses() {
    if (!this.isEquivalence()) {
      return null;
    }
    
    const classes = [];
    const processed = new Set();
    
    Array.from(this.domain).forEach(a => {
      if (!processed.has(a)) {
        const equivalenceClass = new Set([a]);
        
        Array.from(this.domain).forEach(b => {
          if (this.contains(a, b)) {
            equivalenceClass.add(b);
          }
        });
        
        equivalenceClass.forEach(elem => processed.add(elem));
        classes.push(Array.from(equivalenceClass).sort());
      }
    });
    
    return classes;
  }
  
  // Compute transitive closure
  transitiveClosure() {
    const closure = new Relation(Array.from(this.domain), this.getPairs());
    
    let changed = true;
    while (changed) {
      changed = false;
      const currentPairs = closure.getPairs();
      
      for (let [a, b] of currentPairs) {
        for (let [c, d] of currentPairs) {
          if (b === c && !closure.contains(a, d)) {
            closure.addPair(a, d);
            changed = true;
          }
        }
      }
    }
    
    return closure;
  }
  
  // Compute reflexive closure
  reflexiveClosure() {
    const closure = new Relation(Array.from(this.domain), this.getPairs());
    Array.from(this.domain).forEach(a => closure.addPair(a, a));
    return closure;
  }
  
  // Compute symmetric closure
  symmetricClosure() {
    const closure = new Relation(Array.from(this.domain), this.getPairs());
    this.getPairs().forEach(([a, b]) => closure.addPair(b, a));
    return closure;
  }
  
  // Compose with another relation
  compose(otherRelation) {
    const composition = new Relation(Array.from(this.domain));
    
    this.getPairs().forEach(([a, b]) => {
      otherRelation.getPairs().forEach(([c, d]) => {
        if (b === c) {
          composition.addPair(a, d);
        }
      });
    });
    
    return composition;
  }
  
  // Get inverse relation
  inverse() {
    const inverseRel = new Relation(Array.from(this.domain));
    this.getPairs().forEach(([a, b]) => inverseRel.addPair(b, a));
    return inverseRel;
  }
  
  // String representation
  toString() {
    return '{' + this.getPairs().map(pair => '(' + pair.join(',') + ')').join(', ') + '}';
  }
  
  // Matrix representation
  toMatrix() {
    const domainArray = Array.from(this.domain).sort();
    const n = domainArray.length;
    const matrix = Array(n).fill().map(() => Array(n).fill(0));
    
    this.getPairs().forEach(([a, b]) => {
      const i = domainArray.indexOf(a);
      const j = domainArray.indexOf(b);
      if (i !== -1 && j !== -1) {
        matrix[i][j] = 1;
      }
    });
    
    return matrix;
  }
}

// Test the Relation class
const domain = [1, 2, 3];
const pairs = [[1, 1], [1, 2], [2, 1], [2, 2], [3, 3]]; // Not transitive
const relation = new Relation(domain, pairs);

// Check if it's an equivalence relation
console.log(relation.isEquivalence());`,
            expectedOutput: `false`,
            hints: [
              'Store pairs efficiently using string representation',
              'Check reflexive: every element relates to itself',
              'Check symmetric: if aRb then bRa must exist',
              'Check transitive: if aRb and bRc then aRc must exist'
            ]
          }
        },
        {
          id: 'lesson-3',
          title: 'Functions and Mappings',
          type: 'coding-interactive',
          content: 'Understanding functions as special relations and implementing function properties like injectivity and surjectivity.',
          exercise: {
            instructions: 'Create a "MathFunction" class that represents mathematical functions. Implement methods to check if a function is injective (one-to-one), surjective (onto), and bijective. Test with a function that maps domain {1,2,3} to codomain {a,b,c} with specific mappings.',
            initialCode: `class MathFunction {
  constructor(domain, codomain, mappings = {}) {
    this.domain = new Set(domain);
    this.codomain = new Set(codomain);
    this.mappings = new Map();
    
    // Add mappings
    for (let input in mappings) {
      const parsedInput = isNaN(input) ? input : Number(input);
      if (this.domain.has(parsedInput) && this.codomain.has(mappings[input])) {
        this.mappings.set(parsedInput, mappings[input]);
      }
    }
  }
  
  // Add or update a mapping
  map(input, output) {
    if (this.domain.has(input) && this.codomain.has(output)) {
      this.mappings.set(input, output);
    }
    return this;
  }
  
  // Get the output for a given input
  apply(input) {
    return this.mappings.get(input);
  }
  
  // Check if function is well-defined (every domain element has exactly one mapping)
  isWellDefined() {
    return Array.from(this.domain).every(input => this.mappings.has(input));
  }
  
  // Check if function is injective (one-to-one)
  // No two different inputs map to the same output
  isInjective() {
    if (!this.isWellDefined()) return false;
    
    const outputs = Array.from(this.mappings.values());
    const uniqueOutputs = new Set(outputs);
    return outputs.length === uniqueOutputs.size;
  }
  
  // Check if function is surjective (onto)
  // Every element in codomain is mapped to by at least one element in domain
  isSurjective() {
    if (!this.isWellDefined()) return false;
    
    const outputs = new Set(this.mappings.values());
    return outputs.size === this.codomain.size;
  }
  
  // Check if function is bijective (one-to-one and onto)
  isBijective() {
    return this.isInjective() && this.isSurjective();
  }
  
  // Get the range (actual outputs) of the function
  getRange() {
    return new Set(this.mappings.values());
  }
  
  // Get preimage of an element (all inputs that map to this output)
  getPreimage(output) {
    const preimage = [];
    this.mappings.forEach((value, key) => {
      if (value === output) {
        preimage.push(key);
      }
    });
    return preimage;
  }
  
  // Get image of a subset of domain
  getImage(subset) {
    const image = new Set();
    subset.forEach(input => {
      if (this.mappings.has(input)) {
        image.add(this.mappings.get(input));
      }
    });
    return image;
  }
  
  // Compose with another function (this ∘ other)
  compose(otherFunction) {
    // Check if composition is possible
    const otherRange = otherFunction.getRange();
    const thisDomainArray = Array.from(this.domain);
    
    if (!Array.from(otherRange).every(elem => this.domain.has(elem))) {
      throw new Error('Cannot compose: range of first function not subset of domain of second');
    }
    
    const composition = new MathFunction(
      Array.from(otherFunction.domain),
      Array.from(this.codomain)
    );
    
    otherFunction.mappings.forEach((intermediateOutput, input) => {
      if (this.mappings.has(intermediateOutput)) {
        composition.map(input, this.mappings.get(intermediateOutput));
      }
    });
    
    return composition;
  }
  
  // Get inverse function (if it exists)
  getInverse() {
    if (!this.isBijective()) {
      return null; // Inverse only exists for bijective functions
    }
    
    const inverseMappings = {};
    this.mappings.forEach((output, input) => {
      inverseMappings[output] = input;
    });
    
    return new MathFunction(
      Array.from(this.codomain),
      Array.from(this.domain),
      inverseMappings
    );
  }
  
  // Check if this function equals another function
  equals(otherFunction) {
    if (this.domain.size !== otherFunction.domain.size ||
        this.codomain.size !== otherFunction.codomain.size) {
      return false;
    }
    
    return Array.from(this.domain).every(input => {
      return this.apply(input) === otherFunction.apply(input);
    });
  }
  
  // Get function properties as an object
  getProperties() {
    return {
      wellDefined: this.isWellDefined(),
      injective: this.isInjective(),
      surjective: this.isSurjective(),
      bijective: this.isBijective(),
      domainSize: this.domain.size,
      codomainSize: this.codomain.size,
      rangeSize: this.getRange().size
    };
  }
  
  // String representation
  toString() {
    const mappingStrings = [];
    this.mappings.forEach((output, input) => {
      mappingStrings.push(input + ' → ' + output);
    });
    return 'f: {' + Array.from(this.domain).join(', ') + '} → {' + 
           Array.from(this.codomain).join(', ') + '}, ' +
           '{' + mappingStrings.join(', ') + '}';
  }
  
  // Generate all possible functions from domain to codomain
  static generateAllFunctions(domain, codomain) {
    const functions = [];
    const domainArray = Array.from(domain);
    const codomainArray = Array.from(codomain);
    const totalFunctions = Math.pow(codomainArray.length, domainArray.length);
    
    for (let i = 0; i < totalFunctions; i++) {
      const mappings = {};
      let temp = i;
      
      for (let j = domainArray.length - 1; j >= 0; j--) {
        const outputIndex = temp % codomainArray.length;
        mappings[domainArray[j]] = codomainArray[outputIndex];
        temp = Math.floor(temp / codomainArray.length);
      }
      
      functions.push(new MathFunction(domainArray, codomainArray, mappings));
    }
    
    return functions;
  }
}

// Test the MathFunction class
const domain = [1, 2, 3];
const codomain = ['a', 'b', 'c'];
const mappings = {1: 'a', 2: 'b', 3: 'a'}; // Not injective

const func = new MathFunction(domain, codomain, mappings);
console.log(func.isInjective());`,
            expectedOutput: `false`,
            hints: [
              'Store mappings using a Map data structure',
              'Injective means no two inputs have the same output',
              'Surjective means every codomain element is an output',
              'Check that every domain element has exactly one mapping'
            ]
          }
        }
      ],
      assessment: {
        type: 'coding-interactive',
        title: 'Module 2 Assessment: Set Theory Applications',
        instructions: 'Create a "DiscreteStructures" class that combines sets, relations, and functions. Implement methods to find equivalence classes, perform closure operations, and analyze function compositions. Test with complex mathematical structures.',
        initialCode: `class DiscreteStructures {
  constructor() {
    this.sets = new Map();
    this.relations = new Map();
    this.functions = new Map();
  }
  
  // Create and store a set
  createSet(name, elements) {
    this.sets.set(name, new MathSet(elements));
    return this;
  }
  
  // Create and store a relation
  createRelation(name, domain, pairs) {
    this.relations.set(name, new Relation(domain, pairs));
    return this;
  }
  
  // Create and store a function
  createFunction(name, domain, codomain, mappings) {
    this.functions.set(name, new MathFunction(domain, codomain, mappings));
    return this;
  }
  
  // Analyze a relation and return comprehensive information
  analyzeRelation(relationName) {
    const relation = this.relations.get(relationName);
    if (!relation) return null;
    
    const analysis = {
      name: relationName,
      pairs: relation.getPairs(),
      properties: {
        reflexive: relation.isReflexive(),
        symmetric: relation.isSymmetric(),
        antisymmetric: relation.isAntisymmetric(),
        transitive: relation.isTransitive(),
        equivalence: relation.isEquivalence(),
        partialOrder: relation.isPartialOrder()
      },
      closures: {
        reflexive: relation.reflexiveClosure().getPairs(),
        symmetric: relation.symmetricClosure().getPairs(),
        transitive: relation.transitiveClosure().getPairs()
      }
    };
    
    if (analysis.properties.equivalence) {
      analysis.equivalenceClasses = relation.getEquivalenceClasses();
    }
    
    return analysis;
  }
  
  // Analyze a function comprehensively
  analyzeFunction(functionName) {
    const func = this.functions.get(functionName);
    if (!func) return null;
    
    const analysis = {
      name: functionName,
      properties: func.getProperties(),
      range: Array.from(func.getRange()),
      mappings: {}
    };
    
    // Get all mappings
    func.mappings.forEach((output, input) => {
      analysis.mappings[input] = output;
    });
    
    // Get preimages for each codomain element
    analysis.preimages = {};
    func.codomain.forEach(element => {
      analysis.preimages[element] = func.getPreimage(element);
    });
    
    // Try to get inverse if bijective
    if (analysis.properties.bijective) {
      const inverse = func.getInverse();
      analysis.inverse = {};
      inverse.mappings.forEach((output, input) => {
        analysis.inverse[input] = output;
      });
    }
    
    return analysis;
  }
  
  // Perform set operations between stored sets
  performSetOperation(setName1, setName2, operation) {
    const set1 = this.sets.get(setName1);
    const set2 = this.sets.get(setName2);
    
    if (!set1 || !set2) return null;
    
    switch (operation.toLowerCase()) {
      case 'union':
        return set1.union(set2);
      case 'intersection':
        return set1.intersection(set2);
      case 'difference':
        return set1.difference(set2);
      case 'symmetric_difference':
        return set1.symmetricDifference(set2);
      case 'cartesian_product':
        return set1.cartesianProduct(set2);
      default:
        return null;
    }
  }
  
  // Compose two functions
  composeFunctions(function1Name, function2Name) {
    const func1 = this.functions.get(function1Name);
    const func2 = this.functions.get(function2Name);
    
    if (!func1 || !func2) return null;
    
    try {
      return func2.compose(func1); // func2 ∘ func1
    } catch (error) {
      return { error: error.message };
    }
  }
  
  // Find all bijective functions from domain to codomain
  findAllBijections(domain, codomain) {
    if (domain.length !== codomain.length) {
      return []; // No bijections possible if sizes differ
    }
    
    const allFunctions = MathFunction.generateAllFunctions(domain, codomain);
    return allFunctions.filter(func => func.isBijective());
  }
  
  // Partition a set based on an equivalence relation
  partitionByEquivalence(setName, relationName) {
    const set = this.sets.get(setName);
    const relation = this.relations.get(relationName);
    
    if (!set || !relation || !relation.isEquivalence()) {
      return null;
    }
    
    return relation.getEquivalenceClasses();
  }
  
  // Generate power set of a stored set
  generatePowerSet(setName) {
    const set = this.sets.get(setName);
    if (!set) return null;
    
    return set.powerSet();
  }
  
  // Check if a relation is a function
  isRelationAFunction(relationName, codomain) {
    const relation = this.relations.get(relationName);
    if (!relation) return false;
    
    const pairs = relation.getPairs();
    const inputCounts = new Map();
    
    // Count how many times each input appears
    pairs.forEach(([input, output]) => {
      inputCounts.set(input, (inputCounts.get(input) || 0) + 1);
    });
    
    // Every input should appear exactly once
    const domainElements = Array.from(relation.domain);
    return domainElements.every(input => inputCounts.get(input) === 1);
  }
  
  // Convert a function to its corresponding relation
  functionToRelation(functionName) {
    const func = this.functions.get(functionName);
    if (!func) return null;
    
    const pairs = [];
    func.mappings.forEach((output, input) => {
      pairs.push([input, output]);
    });
    
    return new Relation(Array.from(func.domain), pairs);
  }
  
  // Get comprehensive statistics about all structures
  getStatistics() {
    const stats = {
      sets: {
        count: this.sets.size,
        details: {}
      },
      relations: {
        count: this.relations.size,
        equivalenceRelations: 0,
        partialOrders: 0,
        details: {}
      },
      functions: {
        count: this.functions.size,
        injective: 0,
        surjective: 0,
        bijective: 0,
        details: {}
      }
    };
    
    // Analyze sets
    this.sets.forEach((set, name) => {
      stats.sets.details[name] = {
        cardinality: set.cardinality(),
        elements: set.toArray()
      };
    });
    
    // Analyze relations
    this.relations.forEach((relation, name) => {
      const analysis = this.analyzeRelation(name);
      stats.relations.details[name] = analysis;
      if (analysis.properties.equivalence) stats.relations.equivalenceRelations++;
      if (analysis.properties.partialOrder) stats.relations.partialOrders++;
    });
    
    // Analyze functions
    this.functions.forEach((func, name) => {
      const analysis = this.analyzeFunction(name);
      stats.functions.details[name] = analysis;
      if (analysis.properties.injective) stats.functions.injective++;
      if (analysis.properties.surjective) stats.functions.surjective++;
      if (analysis.properties.bijective) stats.functions.bijective++;
    });
    
    return stats;
  }
}

// Helper classes (simplified versions for the test)
class MathSet {
  constructor(elements = []) {
    this.elements = new Set(elements);
  }
  cardinality() { return this.elements.size; }
  union(other) { 
    const result = new MathSet();
    this.elements.forEach(e => result.elements.add(e));
    other.elements.forEach(e => result.elements.add(e));
    return result;
  }
  toArray() { return Array.from(this.elements).sort(); }
}

class Relation {
  constructor(domain, pairs) {
    this.domain = new Set(domain);
    this.pairs = new Set(pairs.map(p => p[0] + ',' + p[1]));
  }
  isEquivalence() { return true; } // Simplified
  getEquivalenceClasses() { return [[1], [2], [3]]; } // Simplified
  getPairs() { return Array.from(this.pairs).map(p => p.split(',').map(Number)); }
}

class MathFunction {
  constructor(domain, codomain, mappings) {
    this.domain = new Set(domain);
    this.codomain = new Set(codomain);
    this.mappings = new Map(Object.entries(mappings).map(([k,v]) => [Number(k), v]));
  }
  isBijective() { return true; } // Simplified
  getProperties() { return { bijective: true }; }
  getRange() { return this.codomain; }
}

// Test the DiscreteStructures class
const ds = new DiscreteStructures();
ds.createSet('A', [1, 2, 3]);
ds.createSet('B', [2, 3, 4]);
const union = ds.performSetOperation('A', 'B', 'union');
console.log(union.cardinality());`,
        expectedOutput: `4`,
        hints: [
          'Combine all previous class implementations',
          'Provide comprehensive analysis methods',
          'Handle error cases for invalid operations',
          'Return detailed statistics and properties'
        ]
      }
    },
    {
      id: 'module-3',
      title: 'Graph Theory Fundamentals',
      description: 'Introduction to graphs, graph representations, traversal algorithms, and basic graph properties.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'lesson-1',
          title: 'Graph Representations and Basic Operations',
          type: 'coding-interactive',
          content: 'Implementing graphs using adjacency lists and matrices, with basic operations like adding vertices and edges.',
          exercise: {
            instructions: 'Create a "Graph" class that supports both directed and undirected graphs. Implement adjacency list and adjacency matrix representations. Include methods to add vertices, add edges, and check connectivity. Test with a graph that has 4 vertices.',
            initialCode: `class Graph {
  constructor(isDirected = false) {
    this.isDirected = isDirected;
    this.vertices = new Set();
    this.adjacencyList = new Map();
    this.adjacencyMatrix = [];
    this.vertexToIndex = new Map();
  }
  
  // Add a vertex to the graph
  addVertex(vertex) {
    if (!this.vertices.has(vertex)) {
      this.vertices.add(vertex);
      this.adjacencyList.set(vertex, new Set());
      
      // Update adjacency matrix
      const index = this.vertices.size - 1;
      this.vertexToIndex.set(vertex, index);
      
      // Expand matrix
      const newSize = this.vertices.size;
      
      // Add new row
      this.adjacencyMatrix.push(new Array(newSize).fill(0));
      
      // Expand existing rows
      for (let i = 0; i < newSize - 1; i++) {
        this.adjacencyMatrix[i].push(0);
      }
    }
    return this;
  }
  
  // Add an edge between two vertices
  addEdge(vertex1, vertex2, weight = 1) {
    // Ensure vertices exist
    this.addVertex(vertex1);
    this.addVertex(vertex2);
    
    // Add to adjacency list
    this.adjacencyList.get(vertex1).add(vertex2);
    if (!this.isDirected && vertex1 !== vertex2) {
      this.adjacencyList.get(vertex2).add(vertex1);
    }
    
    // Update adjacency matrix
    const index1 = this.vertexToIndex.get(vertex1);
    const index2 = this.vertexToIndex.get(vertex2);
    
    this.adjacencyMatrix[index1][index2] = weight;
    if (!this.isDirected && vertex1 !== vertex2) {
      this.adjacencyMatrix[index2][index1] = weight;
    }
    
    return this;
  }
  
  // Remove a vertex and all its edges
  removeVertex(vertex) {
    if (!this.vertices.has(vertex)) return this;
    
    // Remove from adjacency list
    this.adjacencyList.delete(vertex);
    
    // Remove edges to this vertex from other vertices
    this.adjacencyList.forEach((neighbors, v) => {
      neighbors.delete(vertex);
    });
    
    this.vertices.delete(vertex);
    
    // Rebuild adjacency matrix (expensive operation)
    this.rebuildAdjacencyMatrix();
    
    return this;
  }
  
  // Remove an edge
  removeEdge(vertex1, vertex2) {
    if (this.adjacencyList.has(vertex1)) {
      this.adjacencyList.get(vertex1).delete(vertex2);
    }
    
    if (!this.isDirected && this.adjacencyList.has(vertex2)) {
      this.adjacencyList.get(vertex2).delete(vertex1);
    }
    
    // Update adjacency matrix
    const index1 = this.vertexToIndex.get(vertex1);
    const index2 = this.vertexToIndex.get(vertex2);
    
    if (index1 !== undefined && index2 !== undefined) {
      this.adjacencyMatrix[index1][index2] = 0;
      if (!this.isDirected) {
        this.adjacencyMatrix[index2][index1] = 0;
      }
    }
    
    return this;
  }
  
  // Check if there's an edge between two vertices
  hasEdge(vertex1, vertex2) {
    return this.adjacencyList.has(vertex1) && 
           this.adjacencyList.get(vertex1).has(vertex2);
  }
  
  // Get neighbors of a vertex
  getNeighbors(vertex) {
    return this.adjacencyList.has(vertex) ? 
           Array.from(this.adjacencyList.get(vertex)) : [];
  }
  
  // Get degree of a vertex
  getDegree(vertex) {
    if (!this.vertices.has(vertex)) return 0;
    
    if (this.isDirected) {
      // For directed graphs, return in-degree + out-degree
      const outDegree = this.adjacencyList.get(vertex).size;
      let inDegree = 0;
      
      this.adjacencyList.forEach((neighbors, v) => {
        if (neighbors.has(vertex)) inDegree++;
      });
      
      return { inDegree, outDegree, totalDegree: inDegree + outDegree };
    } else {
      return this.adjacencyList.get(vertex).size;
    }
  }
  
  // Check if graph is connected (for undirected graphs)
  isConnected() {
    if (this.vertices.size === 0) return true;
    
    const visited = new Set();
    const start = this.vertices.values().next().value;
    
    this.dfsVisit(start, visited);
    
    return visited.size === this.vertices.size;
  }
  
  // Depth-First Search helper
  dfsVisit(vertex, visited) {
    visited.add(vertex);
    
    this.getNeighbors(vertex).forEach(neighbor => {
      if (!visited.has(neighbor)) {
        this.dfsVisit(neighbor, visited);
      }
    });
  }
  
  // Breadth-First Search
  bfs(startVertex, callback) {
    if (!this.vertices.has(startVertex)) return [];
    
    const visited = new Set();
    const queue = [startVertex];
    const result = [];
    
    visited.add(startVertex);
    
    while (queue.length > 0) {
      const current = queue.shift();
      result.push(current);
      
      if (callback) callback(current);
      
      this.getNeighbors(current).forEach(neighbor => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      });
    }
    
    return result;
  }
  
  // Depth-First Search
  dfs(startVertex, callback) {
    if (!this.vertices.has(startVertex)) return [];
    
    const visited = new Set();
    const result = [];
    
    this.dfsHelper(startVertex, visited, result, callback);
    
    return result;
  }
  
  dfsHelper(vertex, visited, result, callback) {
    visited.add(vertex);
    result.push(vertex);
    
    if (callback) callback(vertex);
    
    this.getNeighbors(vertex).forEach(neighbor => {
      if (!visited.has(neighbor)) {
        this.dfsHelper(neighbor, visited, result, callback);
      }
    });
  }
  
  // Find shortest path (unweighted)
  shortestPath(start, end) {
    if (!this.vertices.has(start) || !this.vertices.has(end)) {
      return null;
    }
    
    const visited = new Set();
    const queue = [[start]]; // Store paths instead of just vertices
    
    visited.add(start);
    
    while (queue.length > 0) {
      const path = queue.shift();
      const current = path[path.length - 1];
      
      if (current === end) {
        return path;
      }
      
      this.getNeighbors(current).forEach(neighbor => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push([...path, neighbor]);
        }
      });
    }
    
    return null; // No path found
  }
  
  // Check for cycles (undirected graph)
  hasCycle() {
    if (this.isDirected) {
      return this.hasCycleDirected();
    } else {
      return this.hasCycleUndirected();
    }
  }
  
  hasCycleUndirected() {
    const visited = new Set();
    
    for (let vertex of this.vertices) {
      if (!visited.has(vertex)) {
        if (this.hasCycleUndirectedHelper(vertex, visited, null)) {
          return true;
        }
      }
    }
    
    return false;
  }
  
  hasCycleUndirectedHelper(vertex, visited, parent) {
    visited.add(vertex);
    
    for (let neighbor of this.getNeighbors(vertex)) {
      if (!visited.has(neighbor)) {
        if (this.hasCycleUndirectedHelper(neighbor, visited, vertex)) {
          return true;
        }
      } else if (neighbor !== parent) {
        return true;
      }
    }
    
    return false;
  }
  
  hasCycleDirected() {
    const visited = new Set();
    const recursionStack = new Set();
    
    for (let vertex of this.vertices) {
      if (!visited.has(vertex)) {
        if (this.hasCycleDirectedHelper(vertex, visited, recursionStack)) {
          return true;
        }
      }
    }
    
    return false;
  }
  
  hasCycleDirectedHelper(vertex, visited, recursionStack) {
    visited.add(vertex);
    recursionStack.add(vertex);
    
    for (let neighbor of this.getNeighbors(vertex)) {
      if (!visited.has(neighbor)) {
        if (this.hasCycleDirectedHelper(neighbor, visited, recursionStack)) {
          return true;
        }
      } else if (recursionStack.has(neighbor)) {
        return true;
      }
    }
    
    recursionStack.delete(vertex);
    return false;
  }
  
  // Get graph statistics
  getStatistics() {
    const numVertices = this.vertices.size;
    const numEdges = this.getEdgeCount();
    
    let totalDegree = 0;
    const degrees = [];
    
    this.vertices.forEach(vertex => {
      const degree = this.isDirected ? 
        this.getDegree(vertex).totalDegree : 
        this.getDegree(vertex);
      degrees.push(degree);
      totalDegree += degree;
    });
    
    return {
      vertices: numVertices,
      edges: numEdges,
      directed: this.isDirected,
      connected: !this.isDirected ? this.isConnected() : null,
      hasCycle: this.hasCycle(),
      averageDegree: numVertices > 0 ? totalDegree / numVertices : 0,
      maxDegree: degrees.length > 0 ? Math.max(...degrees) : 0,
      minDegree: degrees.length > 0 ? Math.min(...degrees) : 0,
      density: numVertices > 1 ? numEdges / (numVertices * (numVertices - 1)) : 0
    };
  }
  
  getEdgeCount() {
    let count = 0;
    this.adjacencyList.forEach(neighbors => {
      count += neighbors.size;
    });
    return this.isDirected ? count : count / 2;
  }
  
  // Rebuild adjacency matrix after vertex removal
  rebuildAdjacencyMatrix() {
    const vertices = Array.from(this.vertices);
    this.vertexToIndex.clear();
    this.adjacencyMatrix = [];
    
    vertices.forEach((vertex, index) => {
      this.vertexToIndex.set(vertex, index);
    });
    
    // Initialize matrix
    const size = vertices.length;
    for (let i = 0; i < size; i++) {
      this.adjacencyMatrix.push(new Array(size).fill(0));
    }
    
    // Fill matrix based on adjacency list
    vertices.forEach((vertex, i) => {
      this.getNeighbors(vertex).forEach(neighbor => {
        const j = vertices.indexOf(neighbor);
        if (j !== -1) {
          this.adjacencyMatrix[i][j] = 1;
        }
      });
    });
  }
  
  // Get adjacency matrix
  getAdjacencyMatrix() {
    return this.adjacencyMatrix.map(row => [...row]); // Return copy
  }
  
  // Get adjacency list as plain object
  getAdjacencyList() {
    const list = {};
    this.adjacencyList.forEach((neighbors, vertex) => {
      list[vertex] = Array.from(neighbors);
    });
    return list;
  }
  
  // String representation
  toString() {
    let result = "Graph (" + (this.isDirected ? "directed" : "undirected") + "):\\n";
    result += "Vertices: " + Array.from(this.vertices).join(", ") + "\\n";
    result += "Edges: ";
    
    const edges = [];
    this.adjacencyList.forEach((neighbors, vertex) => {
      neighbors.forEach(neighbor => {
        if (this.isDirected || vertex <= neighbor) { // Avoid duplicates for undirected
          edges.push(this.isDirected ? vertex + "→" + neighbor : vertex + "↔" + neighbor);
        }
      });
    });
    
    result += edges.join(", ");
    return result;
  }
}

// Test the Graph class
const graph = new Graph(false); // Undirected graph
graph.addVertex('A');
graph.addVertex('B');
graph.addVertex('C');
graph.addVertex('D');
graph.addEdge('A', 'B');
graph.addEdge('B', 'C');
graph.addEdge('C', 'D');

console.log(graph.vertices.size);`,
            expectedOutput: `4`,
            hints: [
              'Use both adjacency list (Map/Set) and matrix representations',
              'Handle both directed and undirected graphs',
              'Implement efficient vertex and edge operations',
              'Provide graph traversal algorithms (BFS, DFS)'
            ]
          }
        }
      ],
      assessment: {
        type: 'coding-interactive',
        title: 'Module 3 Assessment: Graph Applications',
        instructions: 'Create a comprehensive graph analysis system that can detect various graph properties, find shortest paths, and identify connected components. Test with a complex graph structure.',
        initialCode: `// Simplified version for assessment
class GraphAnalyzer {
  constructor() {
    this.graphs = new Map();
  }
  
  createGraph(name, isDirected = false) {
    this.graphs.set(name, new Graph(isDirected));
    return this;
  }
  
  addVertices(graphName, vertices) {
    const graph = this.graphs.get(graphName);
    if (graph) {
      vertices.forEach(v => graph.addVertex(v));
    }
    return this;
  }
  
  addEdges(graphName, edges) {
    const graph = this.graphs.get(graphName);
    if (graph) {
      edges.forEach(([v1, v2]) => graph.addEdge(v1, v2));
    }
    return this;
  }
  
  analyzeGraph(graphName) {
    const graph = this.graphs.get(graphName);
    if (!graph) return null;
    
    return {
      properties: graph.getStatistics(),
      isConnected: !graph.isDirected ? graph.isConnected() : null,
      hasCycle: graph.hasCycle()
    };
  }
}

class Graph {
  constructor(isDirected = false) {
    this.isDirected = isDirected;
    this.vertices = new Set();
    this.adjacencyList = new Map();
  }
  
  addVertex(vertex) {
    if (!this.vertices.has(vertex)) {
      this.vertices.add(vertex);
      this.adjacencyList.set(vertex, new Set());
    }
    return this;
  }
  
  addEdge(vertex1, vertex2) {
    this.addVertex(vertex1);
    this.addVertex(vertex2);
    this.adjacencyList.get(vertex1).add(vertex2);
    if (!this.isDirected) {
      this.adjacencyList.get(vertex2).add(vertex1);
    }
    return this;
  }
  
  getStatistics() {
    return {
      vertices: this.vertices.size,
      edges: this.getEdgeCount(),
      directed: this.isDirected
    };
  }
  
  getEdgeCount() {
    let count = 0;
    this.adjacencyList.forEach(neighbors => {
      count += neighbors.size;
    });
    return this.isDirected ? count : count / 2;
  }
  
  isConnected() {
    if (this.vertices.size === 0) return true;
    const visited = new Set();
    const start = this.vertices.values().next().value;
    this.dfs(start, visited);
    return visited.size === this.vertices.size;
  }
  
  dfs(vertex, visited) {
    visited.add(vertex);
    if (this.adjacencyList.has(vertex)) {
      this.adjacencyList.get(vertex).forEach(neighbor => {
        if (!visited.has(neighbor)) {
          this.dfs(neighbor, visited);
        }
      });
    }
  }
  
  hasCycle() {
    return false; // Simplified for test
  }
}

// Test the analyzer
const analyzer = new GraphAnalyzer();
analyzer.createGraph('test', false);
analyzer.addVertices('test', [1, 2, 3, 4, 5]);
analyzer.addEdges('test', [[1, 2], [2, 3], [3, 4], [4, 5]]);

const analysis = analyzer.analyzeGraph('test');
console.log(analysis.properties.vertices);`,
        expectedOutput: `5`,
        hints: [
          'Implement comprehensive graph analysis methods',
          'Handle both directed and undirected graphs',
          'Provide connectivity and cycle detection',
          'Calculate various graph metrics and properties'
        ]
      }
    },
    {
      id: 'module-4',
      title: 'Combinatorics and Counting',
      description: 'Mathematical counting principles, permutations, combinations, and their computational applications.',
      duration: '3 weeks',
      lessons: [
        {
          id: 'lesson-1',
          title: 'Permutations and Combinations',
          type: 'coding-interactive',
          content: 'Computing permutations and combinations efficiently, with applications to algorithm analysis.',
          exercise: {
            instructions: 'Create a "Combinatorics" class with methods to calculate permutations, combinations, and factorial. Include both recursive and iterative implementations. Test P(5,3) which should return 60.',
            initialCode: `class Combinatorics {
  constructor() {
    this.factorialCache = new Map([[0, 1], [1, 1]]);
  }
  
  // Calculate factorial with memoization
  factorial(n) {
    if (n < 0) return 0;
    if (this.factorialCache.has(n)) {
      return this.factorialCache.get(n);
    }
    
    let result = 1;
    for (let i = 2; i <= n; i++) {
      if (!this.factorialCache.has(i)) {
        this.factorialCache.set(i, this.factorialCache.get(i - 1) * i);
      }
    }
    
    return this.factorialCache.get(n);
  }
  
  // Calculate permutations P(n, r) = n! / (n-r)!
  permutations(n, r) {
    if (r > n || r < 0 || n < 0) return 0;
    if (r === 0) return 1;
    
    // Efficient calculation without computing full factorials
    let result = 1;
    for (let i = n; i > n - r; i--) {
      result *= i;
    }
    return result;
  }
  
  // Calculate combinations C(n, r) = n! / (r! * (n-r)!)
  combinations(n, r) {
    if (r > n || r < 0 || n < 0) return 0;
    if (r === 0 || r === n) return 1;
    
    // Use symmetry property: C(n, r) = C(n, n-r)
    if (r > n - r) {
      r = n - r;
    }
    
    let result = 1;
    for (let i = 0; i < r; i++) {
      result = result * (n - i) / (i + 1);
    }
    return Math.round(result); // Handle floating point precision
  }
  
  // Generate all permutations of an array
  generatePermutations(array) {
    if (array.length <= 1) return [array];
    
    const permutations = [];
    
    for (let i = 0; i < array.length; i++) {
      const current = array[i];
      const remaining = array.slice(0, i).concat(array.slice(i + 1));
      const remainingPerms = this.generatePermutations(remaining);
      
      remainingPerms.forEach(perm => {
        permutations.push([current, ...perm]);
      });
    }
    
    return permutations;
  }
  
  // Generate all combinations of r elements from array
  generateCombinations(array, r) {
    if (r === 0) return [[]];
    if (array.length === 0 || r > array.length) return [];
    
    const combinations = [];
    
    // Include first element
    const withFirst = this.generateCombinations(array.slice(1), r - 1);
    withFirst.forEach(combo => {
      combinations.push([array[0], ...combo]);
    });
    
    // Exclude first element
    const withoutFirst = this.generateCombinations(array.slice(1), r);
    combinations.push(...withoutFirst);
    
    return combinations;
  }
  
  // Calculate multinomial coefficient
  multinomial(n, ...groups) {
    if (groups.reduce((sum, g) => sum + g, 0) !== n) return 0;
    
    let result = this.factorial(n);
    groups.forEach(group => {
      result = result / this.factorial(group);
    });
    
    return Math.round(result);
  }
  
  // Stars and bars method for distributing identical objects
  starsAndBars(n, k) {
    // Number of ways to distribute n identical objects into k distinct boxes
    return this.combinations(n + k - 1, k - 1);
  }
  
  // Derangement: permutations with no element in its original position
  derangements(n) {
    if (n === 0) return 1;
    if (n === 1) return 0;
    if (n === 2) return 1;
    
    let d0 = 1, d1 = 0;
    for (let i = 2; i <= n; i++) {
      const di = (i - 1) * (d0 + d1);
      d0 = d1;
      d1 = di;
    }
    return d1;
  }
  
  // Catalan numbers: C_n = (2n)! / ((n+1)! * n!)
  catalan(n) {
    if (n <= 1) return 1;
    
    // Using recurrence: C_n = sum(C_i * C_(n-1-i)) for i = 0 to n-1
    const catNums = [1, 1]; // C_0 = 1, C_1 = 1
    
    for (let i = 2; i <= n; i++) {
      let sum = 0;
      for (let j = 0; j < i; j++) {
        sum += catNums[j] * catNums[i - 1 - j];
      }
      catNums.push(sum);
    }
    
    return catNums[n];
  }
  
  // Fibonacci numbers (related to counting problems)
  fibonacci(n) {
    if (n <= 1) return n;
    
    let a = 0, b = 1;
    for (let i = 2; i <= n; i++) {
      [a, b] = [b, a + b];
    }
    return b;
  }
  
  // Bell numbers: number of ways to partition a set of n elements
  bell(n) {
    if (n === 0) return 1;
    
    // Bell triangle approach
    const triangle = [[1]];
    
    for (let i = 1; i <= n; i++) {
      const row = [triangle[i - 1][i - 1]]; // Start with last element of previous row
      
      for (let j = 1; j < i; j++) {
        row.push(row[j - 1] + triangle[i - 1][j - 1]);
      }
      
      triangle.push(row);
    }
    
    return triangle[n][0];
  }
  
  // Stirling numbers of the second kind: S(n,k)
  stirlingSecond(n, k) {
    if (n === 0 && k === 0) return 1;
    if (n === 0 || k === 0) return 0;
    if (k > n) return 0;
    
    // Use recurrence: S(n,k) = k*S(n-1,k) + S(n-1,k-1)
    const dp = Array(n + 1).fill().map(() => Array(k + 1).fill(0));
    
    dp[0][0] = 1;
    for (let i = 1; i <= n; i++) {
      for (let j = 1; j <= Math.min(i, k); j++) {
        dp[i][j] = j * dp[i - 1][j] + dp[i - 1][j - 1];
      }
    }
    
    return dp[n][k];
  }
  
  // Pascal's triangle row
  pascalTriangleRow(n) {
    const row = [];
    for (let k = 0; k <= n; k++) {
      row.push(this.combinations(n, k));
    }
    return row;
  }
  
  // Inclusion-exclusion principle for counting
  inclusionExclusion(sets) {
    // Count elements in union of sets using inclusion-exclusion
    const n = sets.length;
    let result = 0;
    
    // Generate all non-empty subsets
    for (let mask = 1; mask < (1 << n); mask++) {
      const subset = [];
      for (let i = 0; i < n; i++) {
        if (mask & (1 << i)) {
          subset.push(sets[i]);
        }
      }
      
      // Find intersection of sets in subset
      let intersection = new Set(subset[0]);
      for (let i = 1; i < subset.length; i++) {
        intersection = new Set([...intersection].filter(x => subset[i].has(x)));
      }
      
      // Apply inclusion-exclusion principle
      if (subset.length % 2 === 1) {
        result += intersection.size;
      } else {
        result -= intersection.size;
      }
    }
    
    return result;
  }
  
  // Count number of integer solutions to equation x1 + x2 + ... + xk = n
  integerSolutions(n, k, nonNegative = true) {
    if (nonNegative) {
      return this.starsAndBars(n, k);
    } else {
      // For unrestricted integer solutions, transform to non-negative case
      // by substituting yi = xi + 1, then solve y1 + y2 + ... + yk = n + k
      return this.starsAndBars(n + k, k);
    }
  }
  
  // Pigeonhole principle calculator
  pigeonholePrinciple(pigeons, holes) {
    return Math.ceil(pigeons / holes);
  }
}

// Test the Combinatorics class
const combo = new Combinatorics();
console.log(combo.permutations(5, 3));`,
            expectedOutput: `60`,
            hints: [
              'Use efficient calculation methods to avoid large factorials',
              'Implement memoization for frequently used values',
              'P(n,r) = n!/(n-r)!, C(n,r) = n!/(r!(n-r)!)',
              'Consider using iterative approaches for better performance'
            ]
          }
        }
      ],
      assessment: {
        type: 'coding-interactive',
        title: 'Module 4 Assessment: Combinatorial Applications',
        instructions: 'Create a comprehensive combinatorial problem solver that can handle various counting problems including partitions, arrangements, and probabilistic scenarios. Test with complex combinatorial problems.',
        initialCode: `class CombinatoricsProblems {
  constructor() {
    this.combinatorics = new Combinatorics();
  }
  
  // Solve committee selection problems
  committeeSelection(totalPeople, committeeSize, restrictions = {}) {
    // Basic combination without restrictions
    let result = this.combinatorics.combinations(totalPeople, committeeSize);
    
    // Handle restrictions (simplified version)
    if (restrictions.mustInclude) {
      const remaining = totalPeople - restrictions.mustInclude.length;
      const remainingNeeded = committeeSize - restrictions.mustInclude.length;
      result = this.combinatorics.combinations(remaining, remainingNeeded);
    }
    
    return result;
  }
  
  // Solve arrangement problems with repetition
  arrangementsWithRepetition(items) {
    const frequencies = {};
    items.forEach(item => {
      frequencies[item] = (frequencies[item] || 0) + 1;
    });
    
    const n = items.length;
    const groups = Object.values(frequencies);
    
    return this.combinatorics.multinomial(n, ...groups);
  }
  
  // Calculate probability combinations
  probabilityCombinations(totalOutcomes, favorableOutcomes) {
    return {
      favorable: favorableOutcomes,
      total: totalOutcomes,
      probability: favorableOutcomes / totalOutcomes
    };
  }
}

class Combinatorics {
  combinations(n, r) {
    if (r > n || r < 0 || n < 0) return 0;
    if (r === 0 || r === n) return 1;
    
    if (r > n - r) r = n - r;
    
    let result = 1;
    for (let i = 0; i < r; i++) {
      result = result * (n - i) / (i + 1);
    }
    return Math.round(result);
  }
  
  multinomial(n, ...groups) {
    if (groups.reduce((sum, g) => sum + g, 0) !== n) return 0;
    
    let result = this.factorial(n);
    groups.forEach(group => {
      result = result / this.factorial(group);
    });
    
    return Math.round(result);
  }
  
  factorial(n) {
    if (n <= 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  }
}

// Test the problem solver
const solver = new CombinatoricsProblems();
const result = solver.committeeSelection(10, 3);
console.log(result);`,
        expectedOutput: `120`,
        hints: [
          'Break down complex problems into basic combinatorial operations',
          'Handle constraints and restrictions systematically',
          'Use appropriate counting principles for each problem type',
          'Consider edge cases and invalid inputs'
        ]
      }
    },
    {
      id: 'module-5',
      title: 'Final Project: Discrete Mathematics Toolkit',
      description: 'Integrate all learned concepts into a comprehensive discrete mathematics computational toolkit.',
      duration: '3 weeks',
      finalProject: {
        type: 'coding-interactive',
        title: 'Final Project: Comprehensive Discrete Mathematics System',
        instructions: 'Create a unified "DiscreteMathToolkit" that integrates logic systems, set operations, graph algorithms, and combinatorial calculations. The system should solve complex problems that span multiple areas of discrete mathematics. Test with a comprehensive mathematical problem.',
        initialCode: `class DiscreteMathToolkit {
  constructor() {
    this.logicSystem = new LogicSystem();
    this.setOperations = new SetOperations();
    this.graphAnalyzer = new GraphAnalyzer();
    this.combinatorics = new Combinatorics();
    this.problemHistory = [];
  }
  
  // Solve complex logic problems
  solveLogicProblem(expressions, variables) {
    const results = {};
    
    expressions.forEach((expr, index) => {
      results["expression_" + index] = {
        truthTable: this.logicSystem.generateTruthTable(expr, variables),
        isTautology: this.logicSystem.isTautology(expr),
        isSatisfiable: this.logicSystem.isSatisfiable(expr),
        simplified: this.logicSystem.simplify(expr)
      };
    });
    
    return results;
  }
  
  // Analyze mathematical structures
  analyzeStructure(type, data) {
    switch (type) {
      case 'set':
        return this.setOperations.analyzeSet(data);
      case 'relation':
        return this.analyzeRelation(data.domain, data.pairs);
      case 'function':
        return this.analyzeFunction(data.domain, data.codomain, data.mappings);
      case 'graph':
        return this.graphAnalyzer.analyzeGraph(data);
      default:
        return { error: 'Unknown structure type' };
    }
  }
  
  // Solve combinatorial optimization problems
  solveCombinatoricsProblem(problemType, parameters) {
    const solution = {
      problemType: problemType,
      parameters: parameters,
      solution: null,
      explanation: ''
    };
    
    switch (problemType) {
      case 'committee_selection':
        solution.solution = this.combinatorics.combinations(
          parameters.totalPeople, 
          parameters.committeeSize
        );
        solution.explanation = "Ways to choose " + parameters.committeeSize + 
          " people from " + parameters.totalPeople + " people";
        break;
        
      case 'arrangement':
        if (parameters.withRepetition) {
          solution.solution = this.combinatorics.multinomial(
            parameters.total, 
            ...parameters.groups
          );
          solution.explanation = "Arrangements of " + parameters.total + 
            " items with repetitions";
        } else {
          solution.solution = this.combinatorics.permutations(
            parameters.n, 
            parameters.r
          );
          solution.explanation = "Arrangements of " + parameters.r + 
            " items from " + parameters.n + " items";
        }
        break;
        
      case 'distribution':
        solution.solution = this.combinatorics.starsAndBars(
          parameters.objects, 
          parameters.containers
        );
        solution.explanation = "Ways to distribute " + parameters.objects + 
          " identical objects into " + parameters.containers + " containers";
        break;
        
      default:
        solution.solution = 0;
        solution.explanation = "Unknown problem type";
    }
    
    this.problemHistory.push(solution);
    return solution;
  }
  
  // Solve graph theory problems
  solveGraphProblem(graphData, problemType) {
    const graph = this.createGraph(graphData);
    const solution = {};
    
    switch (problemType) {
      case 'connectivity':
        solution.isConnected = graph.isConnected();
        solution.connectedComponents = this.findConnectedComponents(graph);
        break;
        
      case 'shortest_path':
        if (graphData.start && graphData.end) {
          solution.shortestPath = graph.shortestPath(graphData.start, graphData.end);
        }
        break;
        
      case 'cycle_detection':
        solution.hasCycle = graph.hasCycle();
        break;
        
      case 'graph_properties':
        solution.properties = graph.getStatistics();
        break;
        
      default:
        solution.error = 'Unknown graph problem type';
    }
    
    return solution;
  }
  
  // Integrate multiple mathematical concepts
  solveIntegratedProblem(problemDescription) {
    const solution = {
      description: problemDescription,
      steps: [],
      finalAnswer: null
    };
    
    // Example: Analyze a social network problem combining graph theory and combinatorics
    if (problemDescription.includes('social_network')) {
      // Step 1: Model as graph
      solution.steps.push({
        step: 1,
        description: "Model social network as graph",
        result: "Graph with " + problemDescription.nodes + " vertices"
      });
      
      // Step 2: Analyze connectivity
      solution.steps.push({
        step: 2,
        description: "Analyze network connectivity",
        result: "Network analysis complete"
      });
      
      // Step 3: Calculate possible connections
      const maxConnections = this.combinatorics.combinations(problemDescription.nodes, 2);
      solution.steps.push({
        step: 3,
        description: "Calculate maximum possible connections",
        result: maxConnections + " possible connections"
      });
      
      solution.finalAnswer = {
        maxConnections: maxConnections,
        networkAnalysis: "Complete analysis performed"
      };
    }
    
    return solution;
  }
  
  // Helper method to create graphs
  createGraph(graphData) {
    // Simplified graph creation
    return {
      vertices: new Set(graphData.vertices || []),
      edges: graphData.edges || [],
      isConnected: () => true,
      shortestPath: (start, end) => [start, end],
      hasCycle: () => false,
      getStatistics: () => ({
        vertices: graphData.vertices?.length || 0,
        edges: graphData.edges?.length || 0
      })
    };
  }
  
  // Analyze relation properties
  analyzeRelation(domain, pairs) {
    return {
      domain: domain,
      pairs: pairs.length,
      properties: {
        reflexive: this.checkReflexive(domain, pairs),
        symmetric: this.checkSymmetric(pairs),
        transitive: this.checkTransitive(pairs)
      }
    };
  }
  
  checkReflexive(domain, pairs) {
    const pairStrings = new Set(pairs.map(p => p[0] + "," + p[1]));
    return domain.every(element => pairStrings.has(element + "," + element));
  }
  
  checkSymmetric(pairs) {
    const pairStrings = new Set(pairs.map(p => p[0] + "," + p[1]));
    return pairs.every(([a, b]) => pairStrings.has(b + "," + a));
  }
  
  checkTransitive(pairs) {
    // Simplified transitivity check
    return true;
  }
  
  // Analyze function properties
  analyzeFunction(domain, codomain, mappings) {
    const mappingEntries = Object.entries(mappings);
    const outputs = mappingEntries.map(([input, output]) => output);
    const uniqueOutputs = new Set(outputs);
    
    return {
      domain: domain.length,
      codomain: codomain.length,
      wellDefined: mappingEntries.length === domain.length,
      injective: outputs.length === uniqueOutputs.size,
      surjective: uniqueOutputs.size === codomain.length,
      bijective: outputs.length === uniqueOutputs.size && uniqueOutputs.size === codomain.length
    };
  }
  
  // Generate comprehensive reports
  generateReport() {
    return {
      problemsSolved: this.problemHistory.length,
      recentProblems: this.problemHistory.slice(-5),
      summary: "Discrete mathematics toolkit operational"
    };
  }
  
  // Educational mode: explain concepts
  explainConcept(concept) {
    const explanations = {
      'logic': 'Propositional logic deals with statements that are either true or false',
      'sets': 'Sets are collections of distinct objects with well-defined operations',
      'relations': 'Relations describe connections between elements of sets',
      'functions': 'Functions are special relations where each input has exactly one output',
      'graphs': 'Graphs model relationships between objects using vertices and edges',
      'combinatorics': 'Combinatorics studies counting, arrangement, and selection problems'
    };
    
    return explanations[concept] || 'Concept not found in knowledge base';
  }
}

// Supporting classes (simplified versions)
class LogicSystem {
  generateTruthTable(expr, vars) { return []; }
  isTautology(expr) { return false; }
  isSatisfiable(expr) { return true; }
  simplify(expr) { return expr; }
}

class SetOperations {
  analyzeSet(data) { return { size: data.length }; }
}

class GraphAnalyzer {
  analyzeGraph(data) { return { vertices: data.vertices?.length || 0 }; }
}

class Combinatorics {
  combinations(n, r) {
    if (r > n || r < 0) return 0;
    if (r === 0 || r === n) return 1;
    if (r > n - r) r = n - r;
    
    let result = 1;
    for (let i = 0; i < r; i++) {
      result = result * (n - i) / (i + 1);
    }
    return Math.round(result);
  }
  
  permutations(n, r) {
    if (r > n || r < 0) return 0;
    let result = 1;
    for (let i = n; i > n - r; i--) {
      result *= i;
    }
    return result;
  }
  
  multinomial(n, ...groups) {
    if (groups.reduce((a, b) => a + b, 0) !== n) return 0;
    
    let result = this.factorial(n);
    groups.forEach(g => result /= this.factorial(g));
    return Math.round(result);
  }
  
  starsAndBars(n, k) {
    return this.combinations(n + k - 1, k - 1);
  }
  
  factorial(n) {
    if (n <= 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) result *= i;
    return result;
  }
}

// Test the comprehensive toolkit
const toolkit = new DiscreteMathToolkit();

// Test combinatorics problem
const solution = toolkit.solveCombinatoricsProblem('committee_selection', {
  totalPeople: 10,
  committeeSize: 3
});

console.log(solution.solution);`,
        expectedOutput: `120`,
        hints: [
          'Integrate all previous modules into one comprehensive system',
          'Provide methods that span multiple mathematical domains',
          'Handle complex problems requiring multiple solution steps',
          'Include educational features and detailed explanations'
        ]
      }
    }
  ],
  assignments: [
    {
      id: 'assignment-1',
      title: 'Logic Circuit Design',
      description: 'Design and implement Boolean logic circuits using the principles learned',
      type: 'project',
      dueDate: 'Week 5',
      points: 75
    },
    {
      id: 'assignment-2',
      title: 'Set Theory Applications',
      description: 'Solve real-world problems using set theory and relations',
      type: 'coding',
      dueDate: 'Week 8',
      points: 50
    },
    {
      id: 'assignment-3',
      title: 'Graph Algorithm Implementation',
      description: 'Implement and analyze graph algorithms for practical applications',
      type: 'coding',
      dueDate: 'Week 11',
      points: 75
    },
    {
      id: 'assignment-4',
      title: 'Combinatorial Optimization',
      description: 'Solve optimization problems using combinatorial methods',
      type: 'project',
      dueDate: 'Week 14',
      points: 50
    }
  ],
  assessments: [
    {
      id: 'midterm',
      title: 'Midterm Examination',
      type: 'exam',
      date: 'Week 8',
      points: 100,
      topics: ['Propositional Logic', 'Set Theory', 'Relations and Functions']
    },
    {
      id: 'final',
      title: 'Final Project Presentation',
      type: 'presentation',
      date: 'Week 15',
      points: 150,
      topics: ['Integrated Discrete Mathematics Applications', 'Graph Theory', 'Combinatorial Problem Solving']
    }
  ]
};